import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

/* Per-user menu-column widths (Session 242).
 *
 * Every menu column (the main Sidebar + every SecondaryColumn) stores its
 * width under a stable key. Writes go to BOTH:
 *   • localStorage  — instant, survives a reload with zero flicker
 *   • the user's account (`/api/dashboard/ui-preferences` → `column_widths`)
 *     so the width follows them to any browser or device.
 *
 * The server read happens ONCE per page load, shared by every mounted column
 * via a module-level promise.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const LS_PREFIX = "column-width:";

let serverWidths = null;       // resolved map, once loaded
let serverPromise = null;      // in-flight request shared by all columns
const pendingWrites = {};      // key → width, flushed together
let flushTimer = null;

const token = () => localStorage.getItem("compass_token");

/** Stable, Mongo-safe key for a column on a given page. */
export function columnWidthKey(path, testId) {
  return `${path}_${testId}`.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 120);
}

function loadServerWidths() {
  if (serverWidths) return Promise.resolve(serverWidths);
  if (!token()) return Promise.resolve({});
  if (!serverPromise) {
    serverPromise = axios
      .get(`${API}/api/dashboard/ui-preferences`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      .then((r) => {
        serverWidths = r.data?.column_widths || {};
        return serverWidths;
      })
      .catch(() => ({}));
  }
  return serverPromise;
}

function queueServerWrite(key, width) {
  pendingWrites[key] = width;
  if (serverWidths) serverWidths[key] = width;
  clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    const payload = { ...pendingWrites };
    Object.keys(pendingWrites).forEach((k) => delete pendingWrites[k]);
    if (!token() || Object.keys(payload).length === 0) return;
    axios.patch(
      `${API}/api/dashboard/ui-preferences`,
      { column_widths: payload },
      { headers: { Authorization: `Bearer ${token()}` } },
    ).catch(() => {});
  }, 500);
}

/**
 * useColumnWidth — resizable width for one menu column, remembered per user.
 *
 * Returns { width, setWidth, startDrag } where `startDrag` is a mousedown
 * handler for the column's resize handle.
 */
export default function useColumnWidth({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  enabled = true,
  onDragChange,
}) {
  const clamp = useCallback(
    (w) => Math.min(maxWidth, Math.max(minWidth, Math.round(w))),
    [minWidth, maxWidth],
  );

  const [width, setWidthState] = useState(() => {
    if (typeof window === "undefined") return defaultWidth;
    const cached = serverWidths?.[storageKey];
    if (typeof cached === "number") return clamp(cached);
    const raw = window.localStorage.getItem(LS_PREFIX + storageKey);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? clamp(n) : defaultWidth;
  });

  // Adopt the account-level width once the shared request resolves. A local
  // value only wins while the user is actively dragging.
  const dragging = useRef(false);
  useEffect(() => {
    let alive = true;
    loadServerWidths().then((map) => {
      const saved = map?.[storageKey];
      if (!alive || dragging.current || typeof saved !== "number") return;
      setWidthState(clamp(saved));
    });
    return () => { alive = false; };
  }, [storageKey, clamp]);

  const persist = useCallback((w) => {
    try { window.localStorage.setItem(LS_PREFIX + storageKey, String(w)); } catch { /* quota */ }
    queueServerWrite(storageKey, w);
  }, [storageKey]);

  const setWidth = useCallback((w) => {
    const next = clamp(w);
    setWidthState(next);
    persist(next);
    return next;
  }, [clamp, persist]);

  // ── Drag ────────────────────────────────────────────────────────────────
  const startX = useRef(0);
  const startWidth = useRef(0);

  const startDrag = useCallback((e) => {
    if (!enabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    onDragChange?.(true);
  }, [enabled, width, onDragChange]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onMove = (e) => {
      if (!dragging.current) return;
      setWidthState(clamp(startWidth.current + (e.clientX - startX.current)));
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      onDragChange?.(false);
      setWidthState((w) => { persist(w); return w; });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [enabled, clamp, persist, onDragChange]);

  return { width, setWidth, startDrag };
}
